// src/components/ImageUploader.jsx
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../services/api';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';

/** Resuelve la URL final: pide URL firmada si es S3, o construye URL local */
function useResolvedUrl(url) {
    const isS3 = url?.includes('amazonaws.com');
    const [resolved, setResolved] = useState(isS3 ? null : (url?.startsWith('http') ? url : `${BASE_URL}${url}`));

    useEffect(() => {
        if (!isS3) return;
        let cancelled = false;
        api.get(`/uploads/signed?url=${encodeURIComponent(url)}`)
            .then(({ data }) => { if (!cancelled) setResolved(data.url); })
            .catch(() => { if (!cancelled) setResolved(url); });
        return () => { cancelled = true; };
    }, [url, isS3]);

    return resolved;
}

const STD_MAX_MB = 8;
const EXT_MAX_MB = 60;

// Tipos aceptados — Word, Excel y PDF en todos los departamentos
const OFFICE_ACCEPT = '.doc,.docx,.xls,.xlsx,.pdf,.csv';
const STD_ACCEPT    = `image/*,${OFFICE_ACCEPT}`;
const EXT_ACCEPT    = `image/*,${OFFICE_ACCEPT},video/mp4,.mp4,.zip`;

function getExt(url = '') {
    return url.split('?')[0].split('.').pop().toLowerCase();
}

/** Miniatura / icono según tipo de archivo */
function FilePreview({ url, onRemove }) {
    const href = useResolvedUrl(url);
    const ext  = getExt(url);

    if (!href) return (
        <div style={{ width: 80, height: 80, borderRadius: 6, border: '1px solid #dee2e6',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner size="sm" animation="border" />
        </div>
    );

    const removeBtn = onRemove && (
        <button
            type="button"
            onClick={onRemove}
            style={{
                position: 'absolute', top: -8, right: -8,
                background: '#dc3545', color: '#fff',
                border: '2px solid #fff', borderRadius: '50%',
                width: 26, height: 26, fontSize: 12,
                lineHeight: '22px', textAlign: 'center',
                cursor: 'pointer', padding: 0,
                boxShadow: '0 1px 3px rgba(0,0,0,.3)',
            }}
            title="Quitar"
            aria-label="Quitar archivo"
        >✕</button>
    );

    // Word
    if (ext === 'doc' || ext === 'docx') {
        return (
            <div style={{ position: 'relative', display: 'inline-block' }}>
                <a href={href} target="_blank" rel="noreferrer"
                   style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                            width: 80, height: 80, justifyContent: 'center',
                            borderRadius: 6, border: '1px solid #dee2e6',
                            background: '#eff6ff', color: '#1d4ed8',
                            textDecoration: 'none', gap: 4 }}>
                    <span style={{ fontSize: 26 }}>📄</span>
                    <span style={{ fontSize: 10, fontWeight: 700 }}>{ext.toUpperCase()}</span>
                </a>
                {removeBtn}
            </div>
        );
    }

    // Excel
    if (ext === 'xls' || ext === 'xlsx') {
        return (
            <div style={{ position: 'relative', display: 'inline-block' }}>
                <a href={href} target="_blank" rel="noreferrer"
                   style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                            width: 80, height: 80, justifyContent: 'center',
                            borderRadius: 6, border: '1px solid #dee2e6',
                            background: '#f0fdf4', color: '#166534',
                            textDecoration: 'none', gap: 4 }}>
                    <span style={{ fontSize: 26 }}>📊</span>
                    <span style={{ fontSize: 10, fontWeight: 700 }}>{ext.toUpperCase()}</span>
                </a>
                {removeBtn}
            </div>
        );
    }

    // CSV
    if (ext === 'csv') {
        return (
            <div style={{ position: 'relative', display: 'inline-block' }}>
                <a href={href} target="_blank" rel="noreferrer"
                   style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                            width: 80, height: 80, justifyContent: 'center',
                            borderRadius: 6, border: '1px solid #dee2e6',
                            background: '#f0fdf4', color: '#166534',
                            textDecoration: 'none', gap: 4 }}>
                    <span style={{ fontSize: 26 }}>📊</span>
                    <span style={{ fontSize: 10, fontWeight: 700 }}>CSV</span>
                </a>
                {removeBtn}
            </div>
        );
    }

    // MP4
    if (ext === 'mp4') {
        return (
            <div style={{ position: 'relative', display: 'inline-block' }}>
                <a href={href} target="_blank" rel="noreferrer"
                   style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                            width: 80, height: 80, justifyContent: 'center',
                            borderRadius: 6, border: '1px solid #dee2e6',
                            background: '#1a1a2e', color: '#a78bfa',
                            textDecoration: 'none', gap: 4 }}>
                    <span style={{ fontSize: 26 }}>🎬</span>
                    <span style={{ fontSize: 10, color: '#c4b5fd' }}>MP4</span>
                </a>
                {removeBtn}
            </div>
        );
    }

    // PDF
    if (ext === 'pdf') {
        return (
            <div style={{ position: 'relative', display: 'inline-block' }}>
                <a href={href} target="_blank" rel="noreferrer"
                   style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                            width: 80, height: 80, justifyContent: 'center',
                            borderRadius: 6, border: '1px solid #dee2e6',
                            background: '#fff1f2', color: '#dc2626',
                            textDecoration: 'none', gap: 4 }}>
                    <span style={{ fontSize: 26 }}>📕</span>
                    <span style={{ fontSize: 10, fontWeight: 700 }}>PDF</span>
                </a>
                {removeBtn}
            </div>
        );
    }

    // ZIP
    if (ext === 'zip') {
        return (
            <div style={{ position: 'relative', display: 'inline-block' }}>
                <a href={href} target="_blank" rel="noreferrer"
                   style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                            width: 80, height: 80, justifyContent: 'center',
                            borderRadius: 6, border: '1px solid #dee2e6',
                            background: '#fef9c3', color: '#854d0e',
                            textDecoration: 'none', gap: 4 }}>
                    <span style={{ fontSize: 26 }}>📦</span>
                    <span style={{ fontSize: 10, color: '#92400e' }}>ZIP</span>
                </a>
                {removeBtn}
            </div>
        );
    }

    // Imagen
    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <a href={href} target="_blank" rel="noreferrer">
                <img
                    src={href}
                    alt="adjunto"
                    style={{ width: 80, height: 80, objectFit: 'cover',
                             borderRadius: 6, border: '1px solid #dee2e6' }}
                />
            </a>
            {removeBtn}
        </div>
    );
}

/**
 * ImageUploader — drag & drop + file browser
 *
 * Props:
 *   urls                : string[]         — URLs ya cargadas (estado externo)
 *   onChange            : (urls) => void   — callback cuando cambia la lista
 *   maxFiles            : number           — máximo de archivos (default 20). Ignorado si allowExtendedUploads=true.
 *   departmentId        : string           — ID del departamento (para la query al backend)
 *   allowExtendedUploads: boolean          — si true, acepta MP4/ZIP, sube el límite a 60 MB y elimina el tope de cantidad
 */
export default function ImageUploader({
    urls          = [],
    onChange,
    maxFiles      = 20,
    departmentId  = '',
    allowExtendedUploads = false,
    globalDrop    = false,   // si true, escucha drops en window y muestra overlay full-screen
}) {
    const inputRef              = useRef(null);
    const [dragging, setDragging]   = useState(false);
    const [uploading, setUploading] = useState(false);
    const [globalDragging, setGlobalDragging] = useState(false);
    const dragCounter = useRef(0);

    const isExtended = !!allowExtendedUploads;
    const maxMB      = isExtended ? EXT_MAX_MB : STD_MAX_MB;
    const maxBytes   = maxMB * 1024 * 1024;
    // Departamentos con uploads extendidos (Diseño Instruccional, Desarrollo y Comunicación)
    // pueden adjuntar tantos archivos como necesiten — el tope de `maxFiles` solo aplica al resto.
    const effectiveMaxFiles = isExtended ? Infinity : maxFiles;

    const uploadFiles = useCallback(async (files) => {
        const remaining = effectiveMaxFiles - urls.length;
        if (remaining <= 0) {
            toast.warn(`Máximo ${maxFiles} archivos`);
            return;
        }
        const toUpload = Array.from(files).slice(0, remaining);
        if (toUpload.length === 0) return;

        setUploading(true);
        const newUrls = [];

        for (const file of toUpload) {
            const ext = file.name.split('.').pop().toLowerCase();

            // Tipos permitidos en todos los departamentos
            const isImg   = file.type.startsWith('image/');
            const isWord  = ['doc', 'docx'].includes(ext) ||
                            ['application/msword',
                             'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
                            .includes(file.type);
            const isExcel = ['xls', 'xlsx'].includes(ext) ||
                            ['application/vnd.ms-excel',
                             'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
                            .includes(file.type);
            const isPdf   = ext === 'pdf' || file.type === 'application/pdf';
            const isCsv   = ext === 'csv' || file.type === 'text/csv';

            // Tipos solo para departamentos extendidos
            const isMp4 = file.type === 'video/mp4' || ext === 'mp4';
            const isZip = ext === 'zip' ||
                          ['application/zip', 'application/x-zip-compressed', 'application/octet-stream']
                          .includes(file.type);

            const allowed = isImg || isWord || isExcel || isPdf || isCsv || (isExtended && (isMp4 || isZip));
            if (!allowed) {
                toast.warn(`"${file.name}": tipo no permitido`);
                continue;
            }

            if (file.size > maxBytes) {
                toast.warn(`"${file.name}" supera el límite de ${maxMB} MB`);
                continue;
            }

            try {
                const form = new FormData();
                form.append('file', file);
                const endpoint = departmentId ? `/uploads?dept=${departmentId}` : '/uploads';
                const { data } = await api.post(endpoint, form, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                newUrls.push(data.url);
            } catch (err) {
                toast.error(err.response?.data?.error || `Error al subir "${file.name}"`);
            }
        }

        setUploading(false);
        if (newUrls.length > 0) onChange([...urls, ...newUrls]);
    }, [urls, onChange, maxFiles, effectiveMaxFiles, isExtended, maxBytes, maxMB, departmentId]);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        uploadFiles(e.dataTransfer.files);
    };

    // Pegar desde portapapeles (Ctrl+V) — solo cuando el foco NO está en un campo de texto
    useEffect(() => {
        const handlePaste = (e) => {
            const tag = e.target?.tagName?.toLowerCase();
            if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
            const files = Array.from(e.clipboardData?.items || [])
                .filter(item => item.kind === 'file')
                .map(item => item.getAsFile())
                .filter(Boolean);
            if (files.length > 0) uploadFiles(files);
        };
        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [uploadFiles]);

    // ── Global drop: escucha drag&drop en TODA la ventana ──────────────────
    // Útil en pantallas de ticket donde el cuadrito de upload puede estar
    // fuera de vista. Usa contador para que dragenter/leave de elementos
    // hijos no hagan parpadear el overlay.
    useEffect(() => {
        if (!globalDrop) return undefined;

        const onDragEnter = (e) => {
            if (!e.dataTransfer?.types?.includes('Files')) return;
            e.preventDefault();
            dragCounter.current += 1;
            setGlobalDragging(true);
        };
        const onDragOver = (e) => {
            if (!e.dataTransfer?.types?.includes('Files')) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
        };
        const onDragLeave = (e) => {
            if (!e.dataTransfer?.types?.includes('Files')) return;
            dragCounter.current = Math.max(0, dragCounter.current - 1);
            if (dragCounter.current === 0) setGlobalDragging(false);
        };
        const onDrop = (e) => {
            if (!e.dataTransfer?.files?.length) return;
            e.preventDefault();
            dragCounter.current = 0;
            setGlobalDragging(false);
            uploadFiles(e.dataTransfer.files);
        };

        window.addEventListener('dragenter', onDragEnter);
        window.addEventListener('dragover',  onDragOver);
        window.addEventListener('dragleave', onDragLeave);
        window.addEventListener('drop',      onDrop);
        return () => {
            window.removeEventListener('dragenter', onDragEnter);
            window.removeEventListener('dragover',  onDragOver);
            window.removeEventListener('dragleave', onDragLeave);
            window.removeEventListener('drop',      onDrop);
        };
    }, [globalDrop, uploadFiles]);

    // En táctil no hay arrastrar ni Ctrl+V: el texto habla de tocar / cámara.
    const isTouch = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const paste = isTouch ? '' : ' · o pega con Ctrl+V';
    const hint = isExtended
        ? `Imágenes, Word, Excel, CSV, PDF, MP4, ZIP · máx ${maxMB} MB · sin límite de archivos${paste}`
        : `Imágenes, Word, Excel, CSV, PDF · máx ${maxMB} MB · hasta ${maxFiles} archivos${paste}`;

    return (
        <div>
            {globalDragging && (
                <div style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(13, 110, 253, .12)',
                    border: '4px dashed #0d6efd',
                    borderRadius: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#0d6efd', fontSize: 22, fontWeight: 700,
                    zIndex: 9999, pointerEvents: 'none',
                }}>
                    📎 Suelta los archivos para adjuntarlos
                </div>
            )}
            <div
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => !uploading && inputRef.current?.click()}
                style={{
                    border: `2px dashed ${dragging ? '#0d6efd' : '#ced4da'}`,
                    borderRadius: 8,
                    padding: '20px 16px',
                    textAlign: 'center',
                    cursor: uploading ? 'default' : 'pointer',
                    background: dragging ? '#f0f4ff' : 'var(--c-input-bg, #fafafa)',
                    transition: 'all 0.15s',
                    userSelect: 'none',
                }}
            >
                {uploading ? (
                    <span className="text-muted">
                        <Spinner size="sm" animation="border" className="me-2" />Subiendo…
                    </span>
                ) : (
                    <span className="text-muted" style={{ fontSize: 14 }}>
                        {isTouch ? 'Toca para ' : 'Arrastra archivos aquí o '}
                        <span style={{ color: '#0d6efd', textDecoration: 'underline' }}>
                            {isTouch ? 'elegir archivo o tomar foto' : 'haz clic para explorar'}
                        </span>
                        <br />
                        <small>{hint}</small>
                    </span>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept={isExtended ? EXT_ACCEPT : STD_ACCEPT}
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => { uploadFiles(e.target.files); e.target.value = ''; }}
                />
            </div>

            {urls.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-2">
                    {urls.map((url, i) => (
                        <FilePreview
                            key={i}
                            url={url}
                            onRemove={() => onChange(urls.filter((_, j) => j !== i))}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/** Muestra adjuntos guardados en modo solo lectura (sin botón de quitar) */
export function AttachmentGrid({ urls = [] }) {
    if (!urls || urls.length === 0) return null;
    return (
        <div className="d-flex flex-wrap gap-2 mt-2">
            {urls.map((url, i) => (
                <FilePreview key={i} url={url} />
            ))}
        </div>
    );
}
